import { mdiDelete, mdiPlus } from "@mdi/js";
import { For, Show } from "@soft-db/soft-utils";
import { Icon, SoftButton, SoftIcon, SoftTypography } from "@soft-db/ui";
import { PropType } from "vue";
import AuditFilterItem from "./AuditFilterItem";
import { AuditFilter, AuditFilterType, PartialAuditFilter } from "./audit-filter.type";

export default defineComponent({
	name: "AuditFilterConfiguration",
	props: {
		filters: {
			type: Array as PropType<AuditFilter[]>,
			default: () => [],
		},
	},
	emits: {
		"update:filters": (_filters: AuditFilter[]) => true,
	},
	setup(props, { emit }) {
		const { t } = useI18n();
		const filters = ref<PartialAuditFilter[]>([]);
		const filtersUnsaved = ref(false);

		const { ignoreUpdates: ignoreUnsavedWatch } = watchIgnorable(
			filters,
			() => {
				filtersUnsaved.value = true;
			},
			{ deep: true }
		);

		watch(
			() => props.filters,
			async () => {
				// Get the original (non-reactive object) and deep clone it
				ignoreUnsavedWatch(() => {
					filters.value = structuredClone(toRaw(props.filters));
				});
			},
			{ immediate: true, deep: true }
		);

		function hasFilter(type: AuditFilterType): boolean {
			return !!filters.value.find((filter) => filter.type === type);
		}
		const hasDateFilter = computed(() => hasFilter("date"));
		const hasTargetFilter = computed(() => hasFilter("target"));
		const hasUserFilter = computed(() => hasFilter("user"));

		const availableTypes = computed<AuditFilterType[]>(() => {
			const types: AuditFilterType[] = [];

			if (!hasDateFilter.value) types.push("date");
			if (!hasTargetFilter.value) types.push("target");
			if (!hasUserFilter.value) types.push("user");

			return types;
		});

		function addFilter() {
			let type: AuditFilterType | null = null;
			if (!hasDateFilter.value) type = "date";
			else if (!hasTargetFilter.value) type = "target";
			else if (!hasUserFilter.value) type = "user";

			if (!type) return;

			filters.value.push({ type });
		}

		function clearAllFilter() {
			filters.value = [];
		}

		const filtersAreValid = computed<boolean>(() => {
			return filters.value.every((filter) => {
				switch (filter.type) {
					case "date":
						return filter.from && filter.to;
					case "target":
						if (!filter.target || !filter.targetId) return false;
						if (filter.target === "alert") return !!filter.parentId;

						return true;
					case "user":
						return !!filter.userId;
				}
			});
		});

		async function saveFilters() {
			if (filtersAreValid.value) {
				// Here, filters are valid se we can type cast them safely
				emit("update:filters", filters.value as AuditFilter[]);
				filtersUnsaved.value = false;
			}
		}

		return () => (
			<div class="flex flex-col gap-2">
				<For each={filters.value}>
					{(filter, i) => (
						<div class="flex items-center gap-2">
							<AuditFilterItem
								class="grow"
								types={availableTypes.value}
								filter={filter}
								onUpdate:filter={(filter: AuditFilter) => (filters.value[i] = filter)}
							/>
							<SoftButton class="mt-5 size-10" ghost round onClick={() => filters.value.splice(i, 1)}>
								<Icon d={mdiDelete} />
							</SoftButton>
						</div>
					)}
				</For>

				<div class="mt-4 flex items-end gap-4">
					<SoftButton outlined disabled={availableTypes.value.length === 0} onClick={addFilter}>
						{t("audit.addFilter")} <SoftIcon small>{mdiPlus}</SoftIcon>
					</SoftButton>
					<div class="grow" />

					<Show when={filtersUnsaved.value}>
						<SoftTypography class="text-error" tag="code">
							{t("audit.filterChanged")}
						</SoftTypography>
					</Show>
					<SoftButton outlined disabled={filters.value.length === 0} onClick={clearAllFilter}>
						{t("common.clear")}
					</SoftButton>
					<SoftButton
						color="secondary"
						disabled={!filtersAreValid.value || !filtersUnsaved.value}
						onClick={saveFilters}
					>
						{t("common.search")}
					</SoftButton>
				</div>
			</div>
		);
	},
});
